import { Component, computed, effect, inject, signal } from '@angular/core';
import { HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { RoleFormFacade } from '../../../../../Facades/role-form.facade';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { Permission, PermissionConfig, PermissionGroup } from '../../../../../Models/avyna/permission.model';
import { MsgService } from '../../../../Shared/Services/msg.service';
import { AvContentContainerComponent } from '../../../../Shared/Components/av-content-container/av-content-container.component';
import { AvLoadingItemComponent } from '../../../../Shared/Components/av-loading-item/av-loading-item.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '../../../../Shared/Services/auth.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { P } from '@angular/cdk/keycodes';
import { PERMISSIONS } from '../../../../../Enums/permission.enum';

@Component({
  selector: 'app-role-form',
  standalone: true,
  imports: [COMMON_IMPORTS, AvContentContainerComponent, AvLoadingItemComponent, MatProgressSpinnerModule],
  templateUrl: './role-form.component.html',
  styleUrls: ['./role-form.component.scss'],
})
export class RoleFormComponent {
  private readonly _facade = inject(RoleFormFacade);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _fb = inject(FormBuilder);
  private readonly _translate = inject(TranslateService);
  private readonly _msgService = inject(MsgService);
  private readonly _authService = inject(AuthService);

  currentUser = toSignal(this._authService.currentUser$, { initialValue: null });
  userPermissions = PERMISSIONS;

  form: FormGroup = this._fb.group({
    name: ['', [Validators.required, Validators.minLength(3)], [this._facade.nameValidator()]],
    permissions: [[], [Validators.required, Validators.minLength(1)]],
  });

  permissions = this._facade.permissions;
  permissionDependencies = this._facade.permissionDependencies;
  permissionConfig = this._facade.permissionConfig;
  readonly role = this._facade.role;
  readonly isLoading = this._facade.isLoading;

  isEditMode = signal(false);
  selectedPermissionIds = signal<Set<number>>(new Set());
  isDirty = signal(false);
  isSubmitting = signal(false);
  permissionSearchTerm = signal('');

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.isDirty()) {
      event.preventDefault();
      event.returnValue = this._translate.instant(
        'app.settings.role_management.form.unsaved_changes',
      );
      return event.returnValue;
    }
    return undefined;
  }

  filteredPermissionConfig = computed((): PermissionConfig[] => {
    const term = this.permissionSearchTerm().trim().toLowerCase();
    const config = this.permissionConfig();
    if (!term) return config;

    const matchedNames = this.getMatchingPermissionNames(term);
    const allNames = this.expandWithDependencies(matchedNames);
    return this.filterConfigByNames(config, allNames);
  });

  private filterConfigByNames(config: PermissionConfig[], names: Set<string>): PermissionConfig[] {
    return config.reduce<PermissionConfig[]>((sections, section) => {
      const filteredGroups = this.filterGroupsByNames(section.groups ?? [], names);
      if (filteredGroups.length) {
        sections.push({ ...section, groups: filteredGroups });
      }
      return sections;
    }, []);
  }

  private filterGroupsByNames(groups: PermissionGroup[], names: Set<string>): PermissionGroup[] {
    return groups.reduce<PermissionGroup[]>((result, group) => {
      const filteredItems = (group.items ?? []).filter(
        (p) => p.name && names.has(p.name)
      );
      if (filteredItems.length) {
        result.push({ ...group, items: filteredItems });
      }
      return result;
    }, []);
  }

  private getMatchingPermissionNames(term: string): Set<string> {
    const matched = new Set<string>();
    const allPerms = this.permissions();

    for (const perm of allPerms) {
      if (!perm.name) continue;

      if (perm.name.toLowerCase().includes(term)) {
        matched.add(perm.name);
        continue;
      }

      if (this.permissionMatchesByTranslation(perm.name, term)) {
        matched.add(perm.name);
      }
    }
    return matched;
  }

  private permissionMatchesByTranslation(permName: string, term: string): boolean {
    const title = this._translate.instant(`shared.models.permissions.${permName}.title`);
    const desc = this._translate.instant(`shared.models.permissions.${permName}.description`);
    return (
      (typeof title === 'string' && title.toLowerCase().includes(term)) ||
      (typeof desc === 'string' && desc.toLowerCase().includes(term))
    );
  }

  private expandWithDependencies(names: Set<string>): Set<string> {
    const result = new Set(names);
    const deps = this.permissionDependencies() as Record<string, string[]>;
    const expand = (name: string) => {
      const parents = deps[name] ?? [];
      for (const parent of parents) {
        if (!result.has(parent)) {
          result.add(parent);
          expand(parent);
        }
      }
    };
    for (const name of names) expand(name);
    return result;
  }

  constructor() {
    this.initForm();
  }

  ngOnInit() {
    this.initMode();
  }

  private initForm() {
    effect(() => {
      if (this.isEditMode() && this.role()) {
        this.initEditForm();
      } else if (!this.isEditMode()) {
        this.form.reset({ name: '', permissions: [] });
        this.selectedPermissionIds.set(new Set<number>());
      }
      this.isDirty.set(false);
    });

    this.form.valueChanges.subscribe(() => this.isDirty.set(true));
  }

  private initEditForm() {
    const selectedIds = new Set<number>();
    const permissions = this.role()?.permissions;
    if (permissions && Array.isArray(permissions)) {
      permissions.forEach((p) => {
        if (p.id !== undefined) selectedIds.add(p.id);
      });
    }
    this.form.patchValue({
      name: this.role()?.name || '',
      permissions: Array.from(selectedIds)
    }, { emitEvent: false });
    this.selectedPermissionIds.set(selectedIds);
  }

  private initMode() {
    this._route.paramMap.subscribe((params) => {
      const roleId = params.get('id');
      if (roleId) {
        this._facade.setRoleId(roleId);
      }
      this.isEditMode.set(!!roleId);
    });
  }

  togglePermission(perm: Permission, isChecked: boolean) {
    if (!perm.name || perm.id === undefined) return;

    this.isDirty.set(true);

    const currentSelection = new Set(this.selectedPermissionIds());

    if (isChecked) {
      this.recursivelySelectParents(perm.name, currentSelection);
    } else {
      this.recursivelyDeselectChildren(perm.name, currentSelection);
    }

    this.selectedPermissionIds.set(currentSelection);
    this.form.get('permissions')?.setValue(Array.from(currentSelection));
    this.form.get('permissions')?.markAsTouched();
  }

  private recursivelySelectParents(permName: string, selected: Set<number>) {
    const permList = this.permissions();
    const perm = permList.find((p) => p.name === permName);
    if (!perm || perm.id === undefined) return;

    selected.add(perm.id);

    const dependenciesMap = this.permissionDependencies() as Record<string, string[]>;
    const parents = dependenciesMap[permName] || [];

    parents.forEach((parentName) => {
      this.recursivelySelectParents(parentName, selected);
    });
  }

  private recursivelyDeselectChildren(permName: string, selected: Set<number>) {
    const permList = this.permissions();
    const perm = permList.find((p) => p.name === permName);
    if (!perm || perm.id === undefined) return;

    selected.delete(perm.id);

    const dependenciesMap = this.permissionDependencies() as Record<string, string[]>;

    permList.forEach((child) => {
      if (child.name) {
        const childDeps = dependenciesMap[child.name] || [];
        if (childDeps.includes(permName)) {
          this.recursivelyDeselectChildren(child.name, selected);
        }
      }
    });
  }

  async onDeleteRole() {
    const confirmed = await this._msgService
      .showConfirm(
        'app.settings.role_management.form.delete_confirm_title',
        'app.settings.role_management.form.delete_confirm_message',
        '',
        'delete',
        'error',
      )
      .afterClosed()
      .toPromise();
    if (!confirmed) return;

    try {
      await this._facade.deleteRole();
      this.isDirty.set(false);
      this._router.navigate(['/settings/roles']);
    } catch (error: any) {
      this._msgService.showSnackBar(error?.error?.message ?? 'shared.general.errors.default', {
        type: 'error',
      });
    }
  }

  async onSubmit() {
    if (this.form.invalid || this.isLoading() || this.isSubmitting()) return;

    if (this.isEditMode() && this.isDirty()) {
      const usersCount = this.role()?.users_count ?? 0;
      if (usersCount >= 1) {
        const title = this._translate.instant('app.settings.role_management.form.users_affected_confirm_title');
        const text = this._translate.instant('app.settings.role_management.form.users_affected_confirm_message', { count: usersCount });
        const confirmed = await this._msgService.showConfirm(title, text, '', 'warning').afterClosed().toPromise();
        if (!confirmed) return;
      }
    }

    this.isSubmitting.set(true);

    const payload = {
      name: this.form.value.name,
      permissionIds: Array.from(this.selectedPermissionIds()),
    };

    try {
      await this._facade.saveRole(payload);

      this.isDirty.set(false);
      this._router.navigate(['/settings/roles']);
    } catch (error: any) {
      this.isSubmitting.set(false);
      this._msgService.showSnackBar(error?.error?.message ?? 'shared.general.errors.default', {
        type: 'error',
      });
    }
  }
}
